import React from 'react'

import { Button } from '../../../main/components/button'
import { DialogBody, DialogFooter } from '../../../main/components/dialog'
import { Paragraph } from '../../../main/components/typography'
import { useFocusInputOnError } from '../../../main/hooks/use-focus-input-on-error'
import { TickerEditColumn } from '../../../main/modules/portfolio/components/EditableColumns/Ticker'
import { PortfolioBulkEditRowContextProvider } from '../../../main/modules/portfolio/components/PortfolioBulkEditRowContext'
import EventCheckboxes from './EventCheckboxes'
import { PriceInput } from './PriceInput'
import { CreateAlertEntry, FormErrors } from './types'
import { submit, validateState } from './utils'

const DEFAULT_STATE: CreateAlertEntry = {
  ticker: '',
  news: false,
  rating: false,
  insider: false,
  priceAbove: '',
  priceBelow: '',
}

type FormContentProps = {
  ticker: string
  onSubmit: () => void
  onClose?: () => void
}

export default function FormContent({ ticker, onSubmit, onClose }: FormContentProps) {
  const [state, setState] = React.useState({ ...DEFAULT_STATE, ticker })
  const [errors, setErrors] = React.useState<FormErrors>({})
  const [apiError, setApiError] = React.useState<string>()
  const formRef = React.useRef<HTMLFormElement>(null)

  useFocusInputOnError(errors)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const { isValid, errors } = await validateState(state)
    setErrors(errors)

    if (!isValid) {
      return
    }

    const alert = Object.assign(
      state,
      typeof state.priceAbove === 'string' && { priceAbove: null },
      typeof state.priceBelow === 'string' && { priceBelow: null }
    )

    const result = await submit(alert)
    if (!result.isSuccess) {
      setApiError(result.message)
    } else {
      onSubmit()
    }
  }

  return (
    <PortfolioBulkEditRowContextProvider hasAlwaysActiveOverride>
      <DialogBody>
        <form
          id="create-alert-form"
          className="space-y-3"
          ref={formRef}
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <Paragraph size="small" className="text-muted-2">
            Get instant email notifications on the selected events.
          </Paragraph>
          {(apiError || errors.isEmpty) && (
            <Paragraph size="small" className="mb-4 text-center text-negative">
              {apiError ?? 'Please choose at least 1 event trigger'}
            </Paragraph>
          )}
          <TickerEditColumn label="Ticker" entry={state} errors={errors} setState={setState} setErrors={setErrors} />
          <EventCheckboxes state={state} setState={setState} setErrors={setErrors} />
          <PriceInput
            name="priceAbove"
            label="Price Above"
            state={state}
            errors={errors}
            setState={setState}
            setErrors={setErrors}
          />
          <PriceInput
            name="priceBelow"
            label="Price Below"
            state={state}
            errors={errors}
            setState={setState}
            setErrors={setErrors}
          />
        </form>
      </DialogBody>
      <DialogFooter>
        {onClose && (
          <Button type="button" onClick={onClose}>
            Close
          </Button>
        )}
        <Button type="submit" form="create-alert-form" theme="blue">
          Save
        </Button>
      </DialogFooter>
    </PortfolioBulkEditRowContextProvider>
  )
}
